<template>
    <div>
      <AdminHeader />
  
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h5 class="page-title">Create New User</h5>
          <p class="page-sub">Add a new employee to your organisation</p>
        </div>
        <button type="button" @click="$router.go(-1)" class="btn-outline">
          <font-awesome-icon icon="arrow-left" /> Back
        </button>
      </div>
  
      <!-- Validation Errors -->
      <div v-if="validationErrors?.length" class="error-banner">
        <font-awesome-icon icon="exclamation-circle" class="error-icon" />
        <ul class="mb-0 ps-1">
          <li v-for="(err, i) in validationErrors" :key="i">{{ err }}</li>
        </ul>
      </div>
  
      <!-- Form Card -->
      <div class="form-card">
        <form @submit.prevent="createUser">
  
          <!-- Personal Info -->
          <div class="section-header">
            <div class="section-icon section-icon--blue">
              <font-awesome-icon icon="user" />
            </div>
            <div>
              <p class="section-title">Personal Information</p>
              <p class="section-sub">Basic details about the new user</p>
            </div>
          </div>
  
          <div class="fields-grid">
            <div class="field-group">
              <label class="field-label">First Name <span class="req">*</span></label>
              <input v-model="first_name" type="text" class="field-input" placeholder="Enter first name" />
            </div>
            <div class="field-group">
              <label class="field-label">Last Name <span class="req">*</span></label>
              <input v-model="last_name" type="text" class="field-input" placeholder="Enter last name" />
            </div>
            <div class="field-group">
              <label class="field-label">Phone Number <span class="req">*</span></label>
              <input v-model="phone_no" type="text" class="field-input" placeholder="e.g. 08012345678" />
            </div>
            <div class="field-group">
              <label class="field-label">Email Address <span class="req">*</span></label>
              <input v-model="email" type="email" class="field-input" placeholder="user@example.com" />
            </div>
          </div>
  
          <div class="section-divider"></div>
  
          <!-- Role & Assignment -->
          <div class="section-header">
            <div class="section-icon section-icon--purple">
              <font-awesome-icon icon="user-shield" />
            </div>
            <div>
              <p class="section-title">Role & Assignment</p>
              <p class="section-sub">Assign a role and link to business locations</p>
            </div>
          </div>
  
          <div class="fields-grid">
            <div class="field-group">
              <label class="field-label">Role <span class="req">*</span></label>
              <select class="field-input field-select" v-model="role_id" @change="getSelectedRole()" id="selectedRole">
                <option value="" disabled>Choose a role</option>
                <option v-for="r in assignableRoles" :key="r.id" :value="r.id">{{ r.name }}</option>
              </select>
            </div>
          </div>
  
          <div class="fields-grid fields-grid--1 mt-3">
            <div class="field-group">
              <label class="field-label">Assigned Business <span class="req">*</span></label>
              <Multiselect
                v-model="business"
                label="name"
                track-by="value"
                :options="business_arr"
                placeholder="Select business(es)"
                mode="tags"
                :searchable="true"
                :create-option="true"
                :close-on-select="false"
                @change="getStores"
                class="custom-multiselect"
              />
            </div>
          </div>
  
          <div class="fields-grid fields-grid--1 mt-3" v-if="store_arr.length">
            <div class="field-group">
              <label class="field-label">Assigned Store <span class="req">*</span></label>
              <Multiselect
                v-model="store"
                label="name"
                :options="store_arr"
                placeholder="Select store(s)"
                mode="tags"
                :searchable="true"
                :create-option="true"
                :close-on-select="false"
                class="custom-multiselect"
              />
            </div>
          </div>
  
          <!-- Footer -->
          <div class="form-footer">
            <button type="button" @click="$router.go(-1)" class="btn-outline">Cancel</button>
            <button type="submit" class="btn-primary-action" :disabled="crudLoading">
              <span v-if="crudLoading" class="spinner-border spinner-border-sm me-2" role="status"></span>
              <font-awesome-icon v-else icon="check" class="me-2" />
              {{ crudLoading ? 'Creating...' : 'Create User' }}
            </button>
          </div>
  
        </form>
      </div>
  
    </div>
  </template>
  
  <style src="@vueform/multiselect/themes/default.css"></style>
  
  <script>
  import { ref, computed } from '@vue/reactivity';
  import AdminHeader from './administrationheadertab.vue';
  import { onMounted, nextTick } from 'vue';
  import axios from 'axios';
  import { useToast } from 'vue-toastification';
  import { useRouter } from 'vue-router';
  import Multiselect from '@vueform/multiselect';
  
  export default {
    components: { AdminHeader, Multiselect },
  
    setup() {
      const first_name = ref('');
      const last_name  = ref('');
      const phone_no   = ref('');
      const email      = ref('');
      const password   = ref('');
      const store      = ref([]);
      const role_id    = ref('');
      const stores     = ref({});
      const store_arr  = ref([]);
      const business      = ref([]);
      const businesses    = ref({});
      const business_arr  = ref([]);
      const crudLoading       = ref(false);
      const roles             = ref([]);
      const toast             = useToast();
      const validationErrors  = ref([]);
      const router            = useRouter();
      const permission_status = ref(null);

      // Roles available for assignment — Super Admin (business_owner) is always hidden.
      const assignableRoles = computed(() =>
        roles.value.filter(r => r.slug !== 'business_owner')
      );
  
      const getRoles = async () => {
        const res = await axios.get('/admin/role');
        roles.value = res.data.data?.data ?? [];
      };
  
      const getStores = async () => {
        await nextTick();
        if (!business.value || business.value.length === 0) { store_arr.value = []; return; }
        const ids = Array.isArray(business.value) ? [...business.value] : [business.value];
        const res = await axios.post('/admin/store/business', { ids });
        store_arr.value = (res.data.data || []).map(s => ({ value: s.id, name: s.store_name }));
      };
  
      const getBusinesses = async () => {
        const res = await axios.get('/admin/business/');
        businesses.value = res.data.data.businesses;
        businesses.value.forEach(b => business_arr.value.push({ value: b.id, name: b.name }));
      };
  
      const getSelectedRole = () => {};
  
      const createUser = async () => {
        crudLoading.value = true;
        await axios.post('/admin/employee/web', {
          first_name: first_name.value,
          last_name:  last_name.value,
          phone_no:   phone_no.value,
          email:      email.value,
          password:   password.value,
          store:      store.value,
          business:   business.value,
          role_id:    role_id.value,
        }).then(() => {
          crudLoading.value = false;
          router.push('/manage-user');
          toast.success('User created successfully');
          validationErrors.value = [];
        }).catch(error => {
          crudLoading.value = false;
          const status = error?.response?.status;
          const msg    = error?.response?.data?.message;

          if (status === 422) {
            validationErrors.value = Array.isArray(msg) ? msg : (msg ? [msg] : ['Validation failed.']);
            toast.warning('Please fix the errors below.');
          } else if (status === 403) {
            permission_status.value = 403;
            toast.error(msg ?? 'You do not have permission to perform this action.');
          } else {
            const displayMsg = typeof msg === 'string' ? msg : 'An error occurred, please try again.';
            validationErrors.value = [displayMsg];
            toast.error(displayMsg);
          }
        });
      };
  
      onMounted(() => { getRoles(); getBusinesses(); });
  
      return {
        first_name, last_name, phone_no, email, password,
        store, role_id, stores, store_arr,
        business, businesses, business_arr,
        crudLoading, roles, assignableRoles, validationErrors,
        createUser, getRoles, getStores, getBusinesses, getSelectedRole,
      };
    },
  };
  </script>
  
  <style scoped>
  /* ── Page Header ── */
  .page-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 20px;
  }
  .page-title { font-size: 20px; font-weight: 700; color: #1a2b45; margin: 0 0 3px; }
  .page-sub   { font-size: 13px; color: #7a90b0; margin: 0; }
  
  /* ── Error Banner ── */
  .error-banner {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    background: #fff5f5;
    border: 1px solid #fac5c5;
    border-radius: 10px;
    padding: 12px 16px;
    margin-bottom: 20px;
    color: #e03535;
    font-size: 13px;
  }
  .error-icon { font-size: 16px; margin-top: 2px; flex-shrink: 0; }
  
  /* ── Form Card ── */
  .form-card {
    background: #fff;
    border: 1px solid #e4eaf2;
    border-radius: 12px;
    padding: 28px;
  }
  
  /* ── Section Header ── */
  .section-header {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 20px;
  }
  .section-icon {
    width: 42px;
    height: 42px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 17px;
    flex-shrink: 0;
  }
  .section-icon--blue   { background: rgba(0,74,173,.1);   color: #004AAD; }
  .section-icon--purple { background: rgba(124,58,237,.1); color: #7c3aed; }
  .section-title { font-size: 14px; font-weight: 700; color: #1a2b45; margin: 0 0 2px; }
  .section-sub   { font-size: 12px; color: #7a90b0; margin: 0; }
  
  /* ── Divider ── */
  .section-divider { height: 1px; background: #f0f4f8; margin: 24px 0; }
  
  /* ── Fields ── */
  .fields-grid     { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
  .fields-grid--1  { grid-template-columns: 1fr; }
  .field-group     { display: flex; flex-direction: column; gap: 6px; }
  .field-label     { font-size: 13px; font-weight: 600; color: #1a2b45; }
  .req             { color: #e03535; }
  
  .field-input {
    padding: 9px 12px;
    border: 1px solid #dde5f0;
    border-radius: 8px;
    font-size: 13.5px;
    color: #1a2b45;
    background: #fff;
    outline: none;
    width: 100%;
    transition: border-color .15s, box-shadow .15s;
  }
  .field-input:focus {
    border-color: #004AAD;
    box-shadow: 0 0 0 3px rgba(0,74,173,.08);
  }
  .field-input::placeholder { color: #aab8cc; }
  .field-select { appearance: auto; cursor: pointer; }
  
  /* Multiselect overrides */
  :deep(.custom-multiselect) {
    --ms-border-color: #dde5f0;
    --ms-border-radius: 8px;
    --ms-font-size: 13.5px;
    --ms-tag-bg: #edf4ff;
    --ms-tag-color: #004AAD;
    --ms-ring-color: rgba(0,74,173,.08);
  }
  :deep(.custom-multiselect.is-active) { --ms-border-color: #004AAD; }
  
  /* ── Footer ── */
  .form-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 28px;
    padding-top: 20px;
    border-top: 1px solid #f0f4f8;
  }
  
  .btn-primary-action {
    display: inline-flex;
    align-items: center;
    background: #004AAD;
    color: #fff;
    font-size: 13px;
    font-weight: 600;
    padding: 9px 24px;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    transition: background .15s;
  }
  .btn-primary-action:hover:not(:disabled) { background: #003b8e; }
  .btn-primary-action:disabled { opacity: .6; cursor: not-allowed; }
  
  .btn-outline {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    background: #fff;
    color: #1a2b45;
    border: 1px solid #dde5f0;
    font-size: 13px;
    font-weight: 500;
    padding: 9px 20px;
    border-radius: 8px;
    cursor: pointer;
    transition: background .15s;
  }
  .btn-outline:hover { background: #f4f7fb; }
  
  /* ── Responsive ── */
  @media (max-width: 768px) {
    .fields-grid    { grid-template-columns: 1fr; }
    .form-card      { padding: 18px; }
    .form-footer    { flex-direction: column; }
    .btn-primary-action,
    .btn-outline    { width: 100%; justify-content: center; }
  }
  </style>